import MojitoNGen from 'mojito/ngen';
import translationService from 'core/services/localization/translation-service';
import uiStyle from 'core/presentation/ui-style';
import { omit, omitBy } from 'mojito/utils';
import { configFactory, META } from 'core/config/config-factory';
import ExecutionMode from 'core/base/execution-mode';

const log = MojitoNGen.logger.get('UIViewUtils');
const shouldOmitProp = (value, key) => key === 'implementation' || value === undefined;

/**
 * Utility class for composing UIView components.
 *
 * @class UIViewUtils
 * @memberof Mojito.Core.Presentation
 */
export default {
    /**
     * Search for component implementation by provided name and contextPath.
     *
     * @example <caption>calling <code>getImplementation('Button', 'inplay')</code> will find MyButtonInplay implementation for _inplay_ context path if the one was configured in viewImplementations object like: <br><br> </caption>
     *
     * const viewImplementations = {
     *      Button: {
     *          implementation: MyButton,
     *          _inplay_implementation: MyButtonInplay,
     *      },
     *  };
     *
     * @example <caption>Will return object:</caption>
     * {
     *     implementation: MyButtonInplay,
     * }
     *
     * @param {string} name - Name of the look-up component.
     * @param {string} contextPath - Current context used to look for component implementation.
     *
     * @returns {Function} Implementation class or function.
     * @function Mojito.Core.Presentation.UIViewUtils.getImplementation
     */
    getImplementation: (name, contextPath) => {
        const implementationConfig = configFactory.getImplementationConfig(name, contextPath);
        return implementationConfig?.implementation;
    },

    /**
     * Search for component's config by name and contextPath, merges found config with config from parent if provided.
     *
     * @param {string} name - Name of the component.
     * @param {string} contextPath - Current context used to look for component config.
     * @param {Mojito.Core.Services.Config.ConfigObject} configOverride - Config that was passed by parent through props.
     *
     * @returns {Mojito.Core.Services.Config.ConfigObject} Contextualized ConfigObject or undefined if no config available.
     * @function Mojito.Core.Presentation.UIViewUtils.getConfig
     */
    getConfig: (name, contextPath, configOverride) => {
        const configMeta = configOverride?.[META];
        const isCompleteConfig = configMeta?.isConfigObject;
        const configName = configMeta?.name;
        if (isCompleteConfig && name !== configName) {
            log.error(`${name} received config for: ${configName}`);
        }
        return isCompleteConfig
            ? configOverride
            : configFactory.getConfig(name, contextPath, configOverride);
    },

    /**
     * Get the translations object for a given language code.
     *
     * @param {string} langCode - The language code used to find the corresponding localization object.
     *
     * @returns {object} The l10n object with localized values.
     * @function Mojito.Core.Presentation.UIViewUtils.getTranslations
     */
    getTranslations(langCode) {
        const l10n = translationService.getTranslations(langCode);
        if (!l10n) {
            log.error(`Failed to load language: ${langCode}`);
        }
        return l10n;
    },

    /**
     * Get component style by executing `getStyleHook`,
     * typically a static `getStyle` function on the component class.
     *
     * @param {Mojito.Core.Services.Config.ConfigObject} config - Config of the related component, supplied to `getStyleHook`.
     * @param {Function} getStyleHook - Function for building the style object, usually defined as static on the component class.
     * @param {string} applicationMode - Passed into `getStyleHook`.
     * @param {string} name - Component name.
     *
     * @returns {object|undefined} Style object ready for use by the component.
     * @function Mojito.Core.Presentation.UIViewUtils.getStyle
     */
    getStyle: (config, getStyleHook, applicationMode, name) => {
        if (!getStyleHook) {
            return;
        }

        try {
            return getStyleHook(config, applicationMode, uiStyle.merge);
        } catch (error) {
            log.error(`${name} style generation failed.`, error);
        }
    },

    /**
     * Verifies that prop types are defined for all incoming props.
     *
     * @param {object} props - Object to verify.
     * @param {object} propTypes - Specifies the type for each property.
     * @param {string} name - Name of the component being verified.
     *
     * @function Mojito.Core.Presentation.UIViewUtils.verifyPropTypes
     */
    verifyPropTypes: (props, propTypes, name) => {
        if (!props || !ExecutionMode.isDebugMode()) {
            return;
        }
        if (Object.keys(props).length > 0 && !propTypes) {
            log.error(`Expected PropTypes for class: ${name}`);
            return;
        }
        // Then check so that all properties have a propType
        Object.keys(props).forEach(key => {
            if (!propTypes.hasOwnProperty(key)) {
                log.error(`Unexpected props name '${key}' for class: ${name}`);
            }
        });
    },

    /**
     * Omits component own props that were provided by UIView HoC: `mojitoTools` and `config` that were set by parent.
     * Typically, these props are for internal use only and not part of the component's public interface.
     * However, we sometimes need to propagate all props to the child.
     *
     * @example // Propagate all props to child - leads to props validation warning.
     * import React from 'react';
     * import RoutingView from './view/index.js';
     *
     * class RoutingController {
     *     render() {
     *          return <RoutingView {...this.props} />;
     *     }
     * }
     *
     * @example // Propagate all props to child with `omitComponentOwnProps` - OK.
     * import React from 'react';
     * import MojitoCore from 'mojito/core';
     * import RoutingView from './view/index.js';
     *
     * const UIViewUtils = MojitoCore.Presentation.UIViewUtils;
     * class RoutingController {
     *     render() {
     *          return <RoutingView {...UIViewUtils.omitComponentOwnProps(this.props)} />;
     *     }
     * }
     *
     * @param {object} props - Component's props object.
     * @param {*} rest - Remaining string-based param keys to be omitted.
     *
     * @returns {object} Props object without component's own properties.
     * @function MojitoCore.Presentation.UIViewUtils.omitComponentOwnProps
     */
    omitComponentOwnProps(props, ...rest) {
        return omit(props, 'mojitoTools', 'config', ...rest);
    },

    /**
     * Logs a warning if props contain properties whose names are reserved for internal Mojito usage.
     *
     * @param {object} props - Props object to be verified.
     * @param {string} sourceName - Name of the component performing the props check.
     * @param {*} reservedPropNames - Any number of prop names reserved for internal use.
     *
     * @function Mojito.Core.Presentation.UIViewUtils.validateReservedProps
     */
    validateReservedProps(props, sourceName, ...reservedPropNames) {
        if (!props || !ExecutionMode.isDebugMode()) {
            return;
        }
        reservedPropNames.forEach(reservedPropName => {
            if (reservedPropName in props) {
                log.warn(
                    `'${reservedPropName}' is reserved prop name and will be overridden by ${sourceName}`
                );
            }
        });
    },

    /**
     * Ensure component default props on props object. The values from defaultProps will be assigned to
     * props object if they are missing on props object of undefined.
     *
     * @param {object} props - Component props object.
     * @param {object} defaultProps - Component default props object.
     *
     * @returns {object} Resulting props object.
     * @function Mojito.Core.Presentation.UIViewUtils.ensureDefaultProps
     */
    ensureDefaultProps(props, defaultProps) {
        return {
            ...defaultProps,
            ...omitBy(props, shouldOmitProp),
        };
    },

    /**
     * Strips redundant top-level route parameter props injected by preact-iso Router.
     *
     * Preact-iso Router passes extracted URL params (such as sportId, eventId) both inside
     * `props.params` and as top-level props on the component. The duplicated top-level keys
     * are not declared in component propTypes, triggering "Unexpected props name" warnings
     * in {@link Mojito.Core.Presentation.UIViewUtils.verifyPropTypes}.
     * This function removes those duplicates while keeping the `params` object itself intact.
     *
     * @param {object} props - Component props potentially containing route params.
     *
     * @returns {object} Props with redundant top-level route param keys removed.
     * @function Mojito.Core.Presentation.UIViewUtils.sanitizeRouteProps
     */
    sanitizeRouteProps(props) {
        const { params } = props;
        if (!params) {
            return props;
        }
        return omit(props, ...Object.keys(params));
    },
};
